import { z } from "zod/v4";

import { RuleGraphCoverageSchema } from "../../../dtos/agentic-logic/rule-graph-coverage";
import { AgenticLogicOnEmptySchema } from "../../../dtos/agentic-logic/rules";
import {
  AgenticLogicValidationScenarioInputSchema,
  AgenticLogicValidationScenarioSchema,
} from "../gates";
import { AgenticLogicRuleLambdaResultSchema } from "../runtime";
import { AgenticLogicExclusionRemovalReasonSchema } from "../types/telemetry";

// ---------------------------------------------------------------------------
// Member routes for the Selection-logic tab's cart-scenario preview:
//
//   GET  /v2/agentic-logic/rules/{ruleId}/scenarios
//   POST /v2/agentic-logic/rules/{ruleId}/evaluate-scenario
//
// Generation itself has no member route here: the frontend calls the ai
// service's generate-for-rule lane directly and persists through save-draft;
// the save-triggered SQS worker covers programmatic (MCP/agent) saves.
//
// Scenario identity on the wire is the scenario `name` — there is no id.
// Evaluation is stateless: the caller posts the full scenario back, so the
// server never resolves a scenario by reference.
// ---------------------------------------------------------------------------

export const AgenticLogicRuleScenariosRequestPathParamsSchema = z.object({
  ruleId: z.uuid(),
});

export type AgenticLogicRuleScenariosRequestPathParamsType = z.infer<
  typeof AgenticLogicRuleScenariosRequestPathParamsSchema
>;

/**
 * `pending` = the generated set is missing or stale for the draft's current
 * source revision; the server has enqueued regeneration and the caller
 * should poll. Authored scenarios (and template-described ones) are always
 * returned regardless of status.
 */
export const AgenticLogicRuleScenariosStatusSchema = z.enum([
  "ready",
  "pending",
]);

export type AgenticLogicRuleScenariosStatusType = z.infer<
  typeof AgenticLogicRuleScenariosStatusSchema
>;

export const GetAgenticLogicRuleScenariosResponseBodySchema = z.object({
  scenarios: z.array(AgenticLogicValidationScenarioSchema),
  status: AgenticLogicRuleScenariosStatusSchema,
  /** The rule's current lexed attempt() base labels, so callers can surface coverage holes. */
  branchIds: z.array(z.string()),
});

export type GetAgenticLogicRuleScenariosResponseBodyType = z.infer<
  typeof GetAgenticLogicRuleScenariosResponseBodySchema
>;

export const EvaluateAgenticLogicRuleScenarioRequestBodySchema = z.object({
  /** The full scenario to run; the server fills request.placementId with the rule's own id when omitted. */
  scenario: AgenticLogicValidationScenarioInputSchema,
});

export type EvaluateAgenticLogicRuleScenarioRequestBodyType = z.infer<
  typeof EvaluateAgenticLogicRuleScenarioRequestBodySchema
>;

/**
 * One envelope item hydrated for display. `price` is pre-formatted with the
 * shop currency ("$24.00") — the renderer never re-derives money. Images are
 * deliberately absent: the admin already has a Shopify GraphQL session, so
 * the renderer resolves the picture from `id`/`variantId` itself rather than
 * having this response carry a second, staler copy of it.
 */
export const AgenticLogicScenarioHydratedItemSchema = z.object({
  /**
   * The envelope item's OWN id, at the item's own grain: a Product GID for a
   * `product` item, a ProductVariant GID for a `variant` item.
   */
  id: z.string(),
  /** The GID of the variant actually displayed, or null when the product has none. */
  variantId: z.string().nullable(),
  title: z.string(),
  /**
   * What distinguishes two rows of the same product ("Ice", "Dawn") — the
   * variant's own title for a variant-grain item, absent otherwise. The
   * serving lane shows the same string on the card. Optional only for
   * replayed tool outputs recorded before the field existed.
   */
  variantTitle: z.string().nullish(),
  price: z.string(),
});

export type AgenticLogicScenarioHydratedItemType = z.infer<
  typeof AgenticLogicScenarioHydratedItemSchema
>;

export const AgenticLogicScenarioExclusionRemovalSchema = z.object({
  itemId: z.string(),
  reason: AgenticLogicExclusionRemovalReasonSchema,
  matchedValue: z.string().nullable(),
});

export type AgenticLogicScenarioExclusionRemovalType = z.infer<
  typeof AgenticLogicScenarioExclusionRemovalSchema
>;

export const EvaluateAgenticLogicRuleScenarioResponseBodySchema = z.object({
  /** Full envelope + telemetry — attempts[], branchFired, items[].sourceBranch. */
  result: AgenticLogicRuleLambdaResultSchema,
  /** Preview-only exact exclusion matches. Optional for recorded responses from before this field existed. */
  exclusions: z
    .object({
      evaluated: z.boolean(),
      removals: z.array(AgenticLogicScenarioExclusionRemovalSchema),
    })
    .optional(),
  /** Telemetry joined onto the rule's graph; null when the rule has no graph artifact. */
  coverage: RuleGraphCoverageSchema.nullable(),
  /** "What the shopper sees": envelope items hydrated for display, in envelope order. What an EMPTY list means is `onEmpty`'s answer, not "hidden". */
  hydratedItems: z.array(AgenticLogicScenarioHydratedItemSchema),
  /**
   * The rule's CURRENT empty-result policy — what an empty envelope renders
   * as: "hide" hides the slot, "smart-recs" fills it with smart
   * recommendations. Optional only for replayed tool outputs recorded before
   * the field existed; the route always answers it.
   */
  onEmpty: AgenticLogicOnEmptySchema.optional(),
  evaluated: z.object({
    source: z.enum(["draft", "published_version", "template"]),
    version: z.number().int().nullable(),
  }),
});

export type EvaluateAgenticLogicRuleScenarioResponseBodyType = z.infer<
  typeof EvaluateAgenticLogicRuleScenarioResponseBodySchema
>;
